package com.devi3east.bashbuddy.prizeList.ui

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ConfirmationNumber
import androidx.compose.material.icons.filled.EmojiEvents
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.dashboard.ui.DashboardUiState
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.ConfirmDeleteDialog
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.prizeList.domain.models.Prize
import com.devi3east.bashbuddy.prizeList.domain.models.PrizeType
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeRequest
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeSeriesRequest
import com.devi3east.bashbuddy.prizeList.requests.UpdatePrizeRequest
import com.devi3east.bashbuddy.prizeList.ui.components.CreatePrizeChoiceDialog
import com.devi3east.bashbuddy.prizeList.ui.components.EmptyPrizeListContent
import com.devi3east.bashbuddy.prizeList.ui.components.PrizeCard
import com.devi3east.bashbuddy.prizeList.ui.components.UpdatePrizeDialog
import kotlinx.datetime.Clock

@Composable
fun PrizeListRoute(
    appState: AppState,
    eventId: String
) {
    val viewModel = appState.dashboardViewModel
    val uiState by viewModel.uiState.collectAsState()

    LaunchedEffect(eventId) {
        viewModel.getPrizesByEventId(eventId)
    }

    val prizesForEvent = uiState.prizeList.filter { it.eventId == eventId }

    PrizeListScreen(
        appState = appState,
        uiState = uiState,
        eventId = eventId,
        prizes = prizesForEvent,
        onCreatePrize = { id, req -> viewModel.createPrize(id, req) },
        onCreatePrizeSeries = { id, req -> viewModel.createPrizeSeries(id, req) },
        onUpdatePrize = { id, req -> viewModel.updatePrize(id, req) },
        onDismissUpdatePrizeDialog = { viewModel.dismissCreatePrizeDialog() },
        onDeletePrize = { id -> viewModel.deletePrize(id) },
        onClearDeleteMessage = { viewModel.clearDeleteMessage() },
        onDismissDeleteDialog = { viewModel.dismissDeleteDialog() },
        onBack = { appState.navController.popBackStack() }
    )
}

@Composable
fun PrizeListScreen(
    appState: AppState,
    uiState: DashboardUiState,
    eventId: String,
    prizes: List<Prize>,
    onCreatePrize: (String, CreatePrizeRequest) -> Unit,
    onCreatePrizeSeries: (String, CreatePrizeSeriesRequest) -> Unit,
    onUpdatePrize: (String, UpdatePrizeRequest) -> Unit,
    onDismissUpdatePrizeDialog: () -> Unit,
    onDeletePrize: (String) -> Unit,
    onClearDeleteMessage: () -> Unit,
    onDismissDeleteDialog: () -> Unit,
    onBack: () -> Unit
) {
    val eventToEdit = uiState.eventList.find { it._id == eventId }

    var selectedTab by remember { mutableStateOf("Prize List") }
    val toggleOptions = listOf("Prize List", "Side Raffles")
    var showCreatePrizeChoiceDialog by remember { mutableStateOf(false) }
    var isCreatingPrize by remember { mutableStateOf(false) }

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
    ) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        Box(modifier = Modifier.fillMaxSize()) {
            when {
                uiState.isPrizeListLoading -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                }

                uiState.errorMessage != null -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = uiState.errorMessage,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }

                else -> {
                    Column(
                        modifier = contentModifier.padding(horizontal = 16.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 8.dp)
                        ) {
                            IconButton(
                                onClick = onBack,
                                modifier = Modifier.align(Alignment.CenterStart)
                            ) {
                                Icon(
                                    imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                    contentDescription = "Back",
                                    tint = MaterialTheme.colorScheme.onSurface
                                )
                            }

                            Row(
                                modifier = Modifier.align(Alignment.Center),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.Center
                            ) {
                                Text(
                                    text = eventToEdit?.title ?: "Unknown Event",
                                    style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                            }
                        }

                        // --- Toggle row ---
                        Row(
                            modifier = Modifier
                                .widthIn(max = targetWidth)
                                .align(Alignment.CenterHorizontally)
                                .padding(horizontal = 16.dp, vertical = 6.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            toggleOptions.forEach { label ->
                                val isSelected = selectedTab == label

                                val backgroundColor by animateColorAsState(
                                    if (isSelected)
                                        MaterialTheme.colorScheme.primaryContainer
                                    else
                                        MaterialTheme.colorScheme.surface.copy(alpha = 0.6f)
                                )

                                val textColor by animateColorAsState(
                                    if (isSelected)
                                        MaterialTheme.colorScheme.onPrimaryContainer
                                    else
                                        MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                                )

                                val cardShape = RoundedCornerShape(12.dp)

                                Surface(
                                    color = backgroundColor,
                                    shape = cardShape,
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                                    modifier = Modifier
                                        .weight(1f)
                                        .clip(cardShape)
                                        .clickable {
                                            selectedTab = label
                                        }
                                        .heightIn(48.dp)
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(vertical = 8.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = label,
                                            color = textColor,
                                            style = MaterialTheme.typography.bodyLarge,
                                            fontWeight = FontWeight.Bold
                                        )
                                    }
                                }
                            }
                        }

                        Spacer(Modifier.height(8.dp))

                        // --- Prize list section ---
                        LazyColumn(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(horizontal = 16.dp),
                            verticalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            when (selectedTab) {
                                "Prize List" -> {
                                    val mainPrizes = prizes
                                        .filter { it.prizeType == PrizeType.MAIN_EVENT }
                                        .sortedBy { it.timeSlotInstant }

                                    if (mainPrizes.isEmpty()) {
                                        item {
                                            EmptyPrizeListContent(
                                                title = "Prize List",
                                                message = "The prize list for this event is empty. Add prizes for your event to display them here and on your event page.",
                                                eventId = eventId,
                                                onCreatePrize = { eid, req -> onCreatePrize(eid, req) },
                                                onShowPrizeChoiceDialog = { showCreatePrizeChoiceDialog = true }
                                            )

                                        }
                                    } else {
                                        items(mainPrizes) { prize ->
                                            PrizeCard(
                                                icon = {
                                                    Icon(
                                                        imageVector = Icons.Default.EmojiEvents,
                                                        contentDescription = null,
                                                        tint = MaterialTheme.colorScheme.primary
                                                    )
                                                },
                                                timeSlot = prize.timeSlotInstant,
                                                prize = prize.title,
                                                winningNumber = prize.winningNumber,
                                                winningNumberVisible = true,
                                                onLongClick = {
                                                    appState.dashboardViewModel.showDeletePrizeDialog(prize._id)
                                                },
                                                onClickCard = {
                                                    appState.dashboardViewModel.openCreatePrizeDialog(
                                                        prize._id,
                                                        isEditing = true
                                                    )
                                                }
                                            )
                                        }
                                    }
                                }

                                "Side Raffles" -> {
                                    val sideRaffles = prizes.filter { it.prizeType == PrizeType.SIDE_RAFFLE }
                                    if (sideRaffles.isEmpty()) {
                                        item {
                                            EmptyPrizeListContent(
                                                title = "Side Raffles",
                                                message = "No side raffles have been created yet. Add items offered outside of the main event prizes to display them here and on your event page.",
                                                eventId = eventId,
                                                onCreatePrize = { eid, req -> onCreatePrize(eid, req) },
                                                onShowPrizeChoiceDialog = {}
                                            )
                                        }
                                    } else {
                                        items(sideRaffles) { prize ->
                                            PrizeCard(
                                                icon = {
                                                    Icon(
                                                        imageVector = Icons.Default.ConfirmationNumber,
                                                        contentDescription = null,
                                                        tint = MaterialTheme.colorScheme.primary
                                                    )
                                                },
                                                serialNumber = prize.serialNumber,
                                                prize = prize.title,
                                                winningNumber = prize.winningNumber,
                                                winningNumberVisible = true,
                                                onLongClick = {
                                                    appState.dashboardViewModel.showDeletePrizeDialog(prize._id)
                                                },
                                                onClickCard = {
                                                    appState.dashboardViewModel.openCreatePrizeDialog(
                                                        prize._id,
                                                        isEditing = true
                                                    )
                                                }
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // --- Confirm delete dialog ---
            if (uiState.isDeletePrizeDialogVisible && uiState.prizeToDeleteId != null) {
                val prizeToDelete = uiState.prizeList.find { it._id == uiState.prizeToDeleteId }

                val deleteTitle = when (prizeToDelete?.prizeType) {
                    PrizeType.SIDE_RAFFLE -> "Delete Side Raffle?"
                    else -> "Delete Prize?"
                }

                val deleteMessage = when (prizeToDelete?.prizeType) {
                    PrizeType.SIDE_RAFFLE -> uiState.deleteMessage.takeUnless { it.isNullOrBlank() }
                        ?: "Are you sure you want to delete this side raffle?"
                    else -> uiState.deleteMessage.takeUnless { it.isNullOrBlank() }
                        ?: "Are you sure you want to delete this prize?"
                }

                ConfirmDeleteDialog(
                    title = deleteTitle,
                    message = deleteMessage,
                    onConfirm = { onDeletePrize(uiState.prizeToDeleteId) },
                    onDismiss = {
                        onDismissDeleteDialog()
                        onClearDeleteMessage()
                    }
                )
            }

            // --- Update prize dialog ---
            if (uiState.isUpdatePrizeDialogVisible && uiState.prizeToUpdateId != null) {
                val prize = uiState.prizeList.find { it._id == uiState.prizeToUpdateId }
                prize?.let {
                    UpdatePrizeDialog(
                        initialInstant = prize.timeSlotInstant ?: eventToEdit?.startInstant ?: Clock.System.now(),
                        prize = it,
                        isEditing = uiState.isEditingPrize,
                        onDismiss = { onDismissUpdatePrizeDialog() },
                        onDelete = { id ->
                            if (!uiState.isEditingPrize) {
                                onDeletePrize(id)
                            }
                            onDismissUpdatePrizeDialog()
                        },
                        onSubmit = { updateReq ->
                            onUpdatePrize(it._id, updateReq)
                            onDismissUpdatePrizeDialog()
                        }
                    )
                }
            }

            if (showCreatePrizeChoiceDialog) {
                CreatePrizeChoiceDialog(
                    eventStartInstant = eventToEdit?.startInstant,
                    onCreateSingle = { type ->
                        showCreatePrizeChoiceDialog = false   // hide dialog immediately
                        isCreatingPrize = true                // show loading overlay immediately
                        onCreatePrize(eventId, CreatePrizeRequest(prizeType = type))
                    },
                    onCreateSeries = { seriesReq ->
                        showCreatePrizeChoiceDialog = false
                        isCreatingPrize = true
                        onCreatePrizeSeries(eventId, seriesReq)
                    },
                    onDismiss = { showCreatePrizeChoiceDialog = false },
                    defaultPrizeType = if (selectedTab == "Side Raffles") PrizeType.SIDE_RAFFLE else PrizeType.MAIN_EVENT
                )
            }

            if (isCreatingPrize) {
                Box(
                    Modifier
                        .fillMaxSize()
                        .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.6f)),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }

                // Watch the prize list; stop loading when new MAIN_EVENT prizes appear
                LaunchedEffect(prizes) {
                    val hasMainEventPrizes = prizes.any { it.prizeType == PrizeType.MAIN_EVENT }
                    if (hasMainEventPrizes) {
                        isCreatingPrize = false
                    }
                }
            }

            FloatingActionButton(
                onClick = {
                    if (selectedTab == "Side Raffles") {
                        isCreatingPrize = true
                        onCreatePrize(eventId, CreatePrizeRequest(prizeType = PrizeType.SIDE_RAFFLE))
                    } else {
                        showCreatePrizeChoiceDialog = true
                    }
                },
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .padding(start = 10.dp, bottom = 10.dp)
            ) {
                Icon(Icons.Default.Add, contentDescription = "Create Prize")
            }
        }
    }
}